import { useState } from 'react'
import { X, Plus, UserPlus } from 'lucide-react'
import {
  useListPersonsQuery,
  useCreatePersonFromFacesMutation,
  useAssignPersonMutation,
} from '@/store/api'
import type { PersonSummary } from '@/store/api'
import { signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface PersonAssignDialogProps {
  faceIds: string[]
  onClose: () => void
  onSuccess: () => void
}

export default function PersonAssignDialog({
  faceIds,
  onClose,
  onSuccess,
}: PersonAssignDialogProps) {
  useSignedUrls()
  const [mode, setMode] = useState<'create' | 'existing'>('create')
  const [newName, setNewName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPerson, setSelectedPerson] = useState<PersonSummary | null>(null)

  const { data: personsData } = useListPersonsQuery({
    pageSize: 100,
    search: searchQuery || undefined,
  })

  const [createPersonFromFaces, { isLoading: isCreating }] = useCreatePersonFromFacesMutation()
  const [assignPerson, { isLoading: isAssigning }] = useAssignPersonMutation()

  const isLoading = isCreating || isAssigning

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        await createPersonFromFaces({
          faceIds,
          name: newName.trim() || undefined,
        }).unwrap()
      } else if (selectedPerson) {
        await assignPerson({
          faceIds,
          personId: selectedPerson.person_id,
        }).unwrap()
      }
      onSuccess()
    } catch (error) {
      const detail = (error as { data?: { detail?: string } })?.data?.detail
      console.error('Failed to assign person:', detail || error)
    }
  }

  const persons = personsData?.persons ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            Group {faceIds.length} Face{faceIds.length !== 1 ? 's' : ''}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setMode('create')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              mode === 'create'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Plus size={14} className="inline mr-1.5" />
            Create New Person
          </button>
          <button
            onClick={() => setMode('existing')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              mode === 'existing'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <UserPlus size={14} className="inline mr-1.5" />
            Add to Existing
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {mode === 'create' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Name (optional)
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter person's name..."
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading) handleSubmit()
                }}
              />
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search persons..."
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 mb-3"
                autoFocus
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {persons.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No persons found. Create one first.
                  </p>
                )}
                {persons.map((person) => (
                  <button
                    key={person.person_id}
                    onClick={() => setSelectedPerson(person)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedPerson?.person_id === person.person_id
                        ? 'bg-cyan-600/20 border border-cyan-500'
                        : 'hover:bg-secondary border border-transparent'
                    }`}
                  >
                    {person.prototype_crop_url ? (
                      <img
                        src={signedSrc(person.prototype_crop_url)}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-secondary" />
                    )}
                    <div className="text-left">
                      <span className="text-foreground font-medium">
                        {person.name || 'Unknown'}
                      </span>
                      <span className="text-muted-foreground ml-2">
                        {person.face_count} face{person.face_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || (mode === 'existing' && !selectedPerson)}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : mode === 'create' ? 'Create Person' : 'Add to Person'}
          </button>
        </div>
      </div>
    </div>
  )
}
